/**
 * bridge/screen_brightness_controller.js — Hotwire Native "screen-brightness"
 * bridge component (web side).
 *
 * Identifier: bridge--screen-brightness (glob-derived from the bridge/ subdir;
 * wire with data-controller="bridge--screen-brightness"). static component =
 * "screen-brightness" MUST equal the iOS ScreenBrightnessComponent `class var
 * name` and the Android-reached method names (boostScreenBrightness /
 * restoreScreenBrightness).
 *
 * Capability tier (custom): temporarily boosts the device screen to max while a
 * payment QR / scan code is on screen, then restores it on dismiss. The web
 * layer owns WHEN (the QR element appears / disappears); native is the hands.
 *
 * It extends the Strada BridgeComponent (so it only connect()s inside the iOS
 * Hotwire Native shell and gets `this.send`) and contributes the
 * `screenBrightness` implementation triple to the window.ChippieBridge facade:
 *
 *   - ios:     send "boost" / "restore" events to ScreenBrightnessComponent.swift
 *   - android: call window.ChippieNative.boostScreenBrightness() /
 *              .restoreScreenBrightness() (no Strada on Android)
 *   - pwa:     no-op (a browser tab cannot change OS screen brightness)
 *
 * Callers do `window.ChippieBridge.screenBrightness(action)` where action is
 * "boost" | "restore". The facade picks the right transport per call.
 *
 * Convenience: when this controller is placed directly on the QR/scan element
 * (data-controller="bridge--screen-brightness"), it auto-boosts on connect and
 * auto-restores on disconnect, so the brightness lifecycle follows the element's
 * presence with no extra wiring. Pages that prefer manual control can instead
 * drive it through the facade and omit the data-controller from the QR element.
 *
 * Outside the iOS shell BridgeComponent never connect()s, so the Android/PWA
 * impls are registered standalone at module load to keep the facade resolvable
 * there; auto boost/restore on element presence is iOS-shell only.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Android impl drives the existing @JavascriptInterface; PWA is a no-op (a web
// tab has no OS brightness control). Neither carries per-instance state, so
// register once at module load. The iOS impl is wired by connect() (it needs a
// live Strada `this.send`). The facade is the SSOT for which one runs.
function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("screenBrightness", {
        // Overridden by connect() with a real Strada send inside the iOS shell;
        // safe no-op if selected before connect.
        ios: () => {},
        android: (action) => {
            if (!window.ChippieNative) return;
            try {
                if (action === "restore" && window.ChippieNative.restoreScreenBrightness) {
                    window.ChippieNative.restoreScreenBrightness();
                } else if (window.ChippieNative.boostScreenBrightness) {
                    window.ChippieNative.boostScreenBrightness();
                }
            } catch (_e) { /* bridge gone */ }
        },
        // A browser tab cannot change OS screen brightness — nothing to do.
        pwa: () => {},
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "screen-brightness";

    connect() {
        super.connect();
        // Inside the iOS shell now: wire the real Strada send into the facade,
        // keeping the Android/PWA fallbacks intact.
        if (window.ChippieBridge && window.ChippieBridge.register) {
            window.ChippieBridge.register("screenBrightness", {
                ios: (action) => this.send(action === "restore" ? "restore" : "boost", {}),
                android: (action) => {
                    if (!window.ChippieNative) return;
                    try {
                        if (action === "restore" && window.ChippieNative.restoreScreenBrightness) {
                            window.ChippieNative.restoreScreenBrightness();
                        } else if (window.ChippieNative.boostScreenBrightness) {
                            window.ChippieNative.boostScreenBrightness();
                        }
                    } catch (_e) { /* bridge gone */ }
                },
                pwa: () => {},
            });
        }

        // The QR/scan element is on screen now — boost. Goes straight to native
        // here (we hold a live `this.send`); the facade is for external callers.
        this.send("boost", {});
    }

    disconnect() {
        // The QR/scan element is leaving — restore the original brightness while
        // we still hold a live bridge instance, before dropping the live-send
        // impl back to the standalone placeholder.
        this.send("restore", {});
        registerStandalone();
        super.disconnect();
    }
}
